import React, { useState } from 'react';
import toast from 'react-hot-toast';

const AboutSection = () => {
  const [stats, setStats] = useState({ properties: 0, satisfaction: 0, experience: 0 });

  // Animate stats when component comes into view
  React.useEffect(() => {
    const animateStats = () => {
      const targets = { properties: 250, satisfaction: 98, experience: 15 };
      const duration = 2000;
      const steps = 60;
      const stepTime = duration / steps;

      let step = 0;
      const interval = setInterval(() => {
        step++;
        const progress = step / steps;
        
        setStats({
          properties: Math.floor(targets.properties * progress),
          satisfaction: Math.floor(targets.satisfaction * progress),
          experience: Math.floor(targets.experience * progress)
        });

        if (step >= steps) {
          clearInterval(interval);
          setStats(targets);
        }
      }, stepTime);
    };

    const timer = setTimeout(animateStats, 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section id="about" className="about-luxhom" role="region" aria-labelledby="about-heading">
      <div className="container">
        <div className="about-content-luxhom">
          <article 
            className="about-text-luxhom"
          >
            <div className="section-badge-luxhom">ABOUT TRUEHOME</div>
            <h2 id="about-heading">Redefining Luxury Real Estate Across India</h2>
            <p>
              For over 15 years, TrueHome Properties has been at the forefront of luxury real estate, 
              connecting discerning clients with exceptional properties across Karnataka, Goa, Andhra Pradesh, 
              Tamil Nadu, Pune, and Mumbai. Our commitment to excellence and data-driven approach has made us 
              the trusted choice for property buyers and sellers throughout these regions.
            </p>
            <p>
              We combine deep market knowledge with cutting-edge analytics and technology to deliver 
              unparalleled results. Our comprehensive digital marketing strategies and performance 
              tracking ensure maximum visibility for your property investments.
            </p>

            <div className="about-stats-luxhom" role="list" aria-label="Company statistics">
              <div 
                className="stat-luxhom"
                role="listitem"
              >
                <span className="stat-number-luxhom" aria-label={`${stats.properties} plus properties sold`}>{stats.properties}+</span>
                <span className="stat-label-luxhom">Properties Sold</span>
              </div>
              <div 
                className="stat-luxhom"
                role="listitem"
              >
                <span className="stat-number-luxhom" aria-label={`${stats.satisfaction} percent client satisfaction`}>{stats.satisfaction}%</span>
                <span className="stat-label-luxhom">Client Satisfaction</span>
              </div>
              <div 
                className="stat-luxhom"
                role="listitem"
              >
                <span className="stat-number-luxhom" aria-label={`${stats.experience} plus years experience`}>{stats.experience}+</span>
                <span className="stat-label-luxhom">Years Experience</span>
              </div>
            </div>
          </article>

          <figure 
            className="about-image-luxhom"
          >
            <img 
              src="/images/truehome.jpg" 
              alt="TrueHome Properties office building - Your trusted real estate partner for BDA and BRMDA approved properties across Karnataka, Goa, Andhra Pradesh, Tamil Nadu, Pune, and Mumbai" 
              loading="lazy"
            />
          </figure>
        </div>

        {/* Vision and Mission Section */}
        <div className="vision-mission-wrapper">
          <header 
            className="vm-section-header"
          >
            <div className="vm-badge">OUR FOUNDATION</div>
            <h2 id="vision-mission-heading">Vision & Mission</h2>
            <p>Guiding principles that drive our commitment to excellence in real estate</p>
          </header>

          <div className="vision-mission-section" role="region" aria-labelledby="vision-mission-heading">
            <article 
              className="vision-mission-card vision-card"
            >
              <div className="vm-card-inner">
                <div className="vm-header">
                  <div className="vm-icon-wrapper">
                    <div className="vm-icon">
                      <i className="fas fa-eye"></i>
                    </div>
                    <div className="vm-icon-bg"></div>
                  </div>
                  <h3>Our Vision</h3>
                </div>
                
                <div className="vm-content">
                  <p>
                    To be India's most trusted and innovative real estate partner, revolutionizing the 
                    property landscape through cutting-edge technology, transparent practices, and personalized 
                    service. We envision a future where every individual and family discovers their perfect 
                    property seamlessly across Karnataka, Goa, Andhra Pradesh, Tamil Nadu, Pune, and Mumbai—whether 
                    it's BDA approved sites, BRMDA properties, DC conversions, revenue sites, residential houses, 
                    agricultural land, or commercial sheds.
                  </p>
                  
                  <div className="vm-features">
                    <div className="vm-feature-item">
                      <div className="feature-icon">
                        <i className="fas fa-rocket"></i>
                      </div>
                      <div className="feature-text">
                        <h4>Innovation First</h4>
                        <span>Technology-driven solutions</span>
                      </div>
                    </div>
                    <div className="vm-feature-item">
                      <div className="feature-icon">
                        <i className="fas fa-shield-alt"></i>
                      </div>
                      <div className="feature-text">
                        <h4>Complete Trust</h4>
                        <span>100% transparency guaranteed</span>
                      </div>
                    </div>
                    <div className="vm-feature-item">
                      <div className="feature-icon">
                        <i className="fas fa-users"></i>
                      </div>
                      <div className="feature-text">
                        <h4>Client Focus</h4>
                        <span>Your success is our priority</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </article>

            <article 
              className="vision-mission-card mission-card"
            >
              <div className="vm-card-inner">
                <div className="vm-header">
                  <div className="vm-icon-wrapper">
                    <div className="vm-icon">
                      <i className="fas fa-bullseye"></i>
                    </div>
                    <div className="vm-icon-bg"></div>
                  </div>
                  <h3>Our Mission</h3>
                </div>
                
                <div className="vm-content">
                  <p>
                    To deliver comprehensive real estate solutions with unwavering integrity and excellence. 
                    We specialize in all property types including BDA and BRMDA approved sites, DC conversions, 
                    revenue sites, independent houses, residential and agricultural land, and industrial sheds—all 
                    available for purchase or rent. Beyond property transactions, we provide expert architectural 
                    planning and construction services at discounted rates.
                  </p>
                  
                  <div className="vm-features">
                    <div className="vm-feature-item">
                      <div className="feature-icon">
                        <i className="fas fa-home"></i>
                      </div>
                      <div className="feature-text">
                        <h4>All Property Types</h4>
                        <span>BDA, BRMDA, DC & more</span>
                      </div>
                    </div>
                    <div className="vm-feature-item">
                      <div className="feature-icon">
                        <i className="fas fa-percentage"></i>
                      </div>
                      <div className="feature-text">
                        <h4>Best Value</h4>
                        <span>Discounted construction rates</span>
                      </div>
                    </div>
                    <div className="vm-feature-item">
                      <div className="feature-icon">
                        <i className="fas fa-hands-helping"></i>
                      </div>
                      <div className="feature-text">
                        <h4>Full Support</h4>
                        <span>End-to-end assistance</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;